import { useEffect, useRef, useState } from 'react';
import {
  HeartPulse,
  Check,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
  RotateCw,
  Loader2,
  Clock,
  Circle,
} from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { usePluginHealth, useRunPluginDependencyCheck } from '@/hooks/config/usePluginHealth';
import type { PluginManifestHealthChecks } from '@/lib/plugin-registry';
import type { PluginHealthCheckEntry } from '@reflex/plugin-api';

interface PluginHealthSectionProps {
  pluginName: string;
  healthChecks?: PluginManifestHealthChecks;
  /** When true, runs a dependency check once after initial health data loads. Defaults to false. */
  autoRunDependencyCheckOnLoad?: boolean;
}

function formatTimestamp(timestamp: number | undefined): string {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatRelativeTime(timestamp: number | undefined): string {
  if (!timestamp) return '';
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StatusBadge({ status }: { status: PluginHealthCheckEntry['status'] }) {
  switch (status) {
    case 'ok':
      return (
        <Badge
          variant="secondary"
          className="gap-1 bg-status-success/10 text-xs font-normal text-status-success"
          data-status="ok"
        >
          <Check className="h-3 w-3" /> OK
        </Badge>
      );
    case 'degraded':
      return (
        <Badge
          variant="secondary"
          className="gap-1 bg-status-accent-orange/10 text-xs font-normal text-status-accent-orange"
          data-status="degraded"
        >
          <AlertTriangle className="h-3 w-3" /> Degraded
        </Badge>
      );
    case 'failed':
      return (
        <Badge
          variant="secondary"
          className="gap-1 bg-status-error/10 text-xs font-normal text-status-error"
          data-status="failed"
        >
          <XCircle className="h-3 w-3" /> Failed
        </Badge>
      );
  }
}

/** Per-peer liveness entry from the host-derived dependency aggregate. */
interface PeerEntry {
  name: string;
  status: 'ok' | 'degraded' | 'failed' | 'unknown';
  message?: string;
  timestamp?: number;
}

function PeerStatusBadge({ status }: { status: PeerEntry['status'] }) {
  switch (status) {
    case 'ok':
      return <Check className="h-3 w-3 text-status-success" aria-label="OK" />;
    case 'degraded':
      return <AlertTriangle className="h-3 w-3 text-status-accent-orange" aria-label="Degraded" />;
    case 'failed':
      return <XCircle className="h-3 w-3 text-status-error" aria-label="Failed" />;
    case 'unknown':
      return <Circle className="h-3 w-3 text-muted-foreground" aria-label="Unknown" />;
  }
}

function PeerBreakdown({ peers }: { peers: PeerEntry[] }) {
  if (peers.length === 0) {
    return (
      <div className="border-t bg-muted/30 px-4 py-3">
        <span className="text-xs text-muted-foreground">No declared dependencies</span>
      </div>
    );
  }

  return (
    <div className="border-t bg-muted/30 divide-y">
      {peers.map((peer) => (
        <div key={peer.name} className="flex items-center gap-2 px-4 py-2">
          <PeerStatusBadge status={peer.status} />
          <span className="text-xs font-medium">{peer.name}</span>
          {peer.message && (
            <span className="text-xs text-muted-foreground truncate">{peer.message}</span>
          )}
          {peer.timestamp && (
            <span
              className="text-xs text-muted-foreground ml-auto"
              title={formatTimestamp(peer.timestamp)}
            >
              {formatRelativeTime(peer.timestamp)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function HealthCheckRow({
  label,
  entry,
  configured = true,
  action,
  testId,
  alwaysExpandIfContent = false,
}: {
  label: string;
  entry: PluginHealthCheckEntry | null;
  configured?: boolean;
  action?: React.ReactNode;
  testId: string;
  /** When true, expand by default whenever there is content to show (peer dependencies). */
  alwaysExpandIfContent?: boolean;
}) {
  const hasDetails = entry?.details && Object.keys(entry.details).length > 0;
  const peers = entry?.details?.peers as PeerEntry[] | undefined;
  const hasPeers = Array.isArray(peers);
  const canExpand = hasDetails || hasPeers;

  // Auto-expand: peer dependencies row always expands if content exists;
  // startup/liveness expand only when status is not ok and there's content to show.
  const shouldDefaultExpand =
    canExpand && (alwaysExpandIfContent || (entry != null && entry.status !== 'ok'));
  const [expanded, setExpanded] = useState(shouldDefaultExpand);

  if (!entry) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid={testId}>
        <div className="flex items-center gap-2">
          {configured ? (
            <Clock className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Circle className="h-4 w-4 text-muted-foreground/70" />
          )}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {action}
          <span className="text-xs text-muted-foreground">
            {configured ? 'Not run yet' : 'N/A'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {canExpand ? (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-left"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label} details`}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">{label}</span>
            </button>
          ) : (
            <>
              <div className="h-4 w-4" />
              <span className="text-sm font-medium">{label}</span>
            </>
          )}
          <StatusBadge status={entry.status} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          {entry.timestamp && (
            <span
              className="text-xs text-muted-foreground"
              title={formatTimestamp(entry.timestamp)}
            >
              {formatRelativeTime(entry.timestamp)}
            </span>
          )}
        </div>
      </div>
      {entry.message && (
        <PageDescription className="mt-0 wrap-break-word px-4 pb-3 pl-10 text-xs">
          {entry.message}
        </PageDescription>
      )}
      {expanded && hasPeers && <PeerBreakdown peers={peers} />}
      {expanded && hasDetails && !hasPeers && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <pre className="overflow-auto text-xs text-muted-foreground">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function PluginHealthSection({
  pluginName,
  healthChecks,
  autoRunDependencyCheckOnLoad = false,
}: PluginHealthSectionProps) {
  const {
    data: health,
    isLoading,
    isError: queryError,
    error: queryErrorData,
  } = usePluginHealth(pluginName);
  const dependencyCheck = useRunPluginDependencyCheck(pluginName);
  const { mutate: runDependencyCheck } = dependencyCheck;
  const autoDependencyStartedRef = useRef(false);
  const hasDependencyCheck = healthChecks?.dependencies ?? health?.dependencies != null;

  useEffect(() => {
    autoDependencyStartedRef.current = false;
  }, [pluginName]);

  useEffect(() => {
    if (
      !autoRunDependencyCheckOnLoad ||
      !hasDependencyCheck ||
      isLoading ||
      !health ||
      autoDependencyStartedRef.current
    )
      return;
    autoDependencyStartedRef.current = true;
    runDependencyCheck();
  }, [autoRunDependencyCheckOnLoad, hasDependencyCheck, isLoading, health, runDependencyCheck]);

  if (isLoading) {
    return (
      <section data-testid="plugin-detail-section-health">
        <IconSectionHeader icon={HeartPulse}>Health</IconSectionHeader>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading health status...
        </div>
      </section>
    );
  }

  if (queryError) {
    return (
      <section data-testid="plugin-detail-section-health">
        <IconSectionHeader icon={HeartPulse}>Health</IconSectionHeader>
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Failed to load health status: {queryErrorData?.message ?? 'Unknown error'}</span>
        </div>
      </section>
    );
  }

  const hasDependencyResult = health?.dependencies != null;
  const dependencyButtonLabel = dependencyCheck.isPending
    ? 'Refreshing peer dependencies'
    : hasDependencyResult
      ? 'Refresh peer dependencies'
      : 'Check peer dependencies';

  return (
    <section data-testid="plugin-detail-section-health">
      <IconSectionHeader icon={HeartPulse}>Health</IconSectionHeader>
      {dependencyCheck.isError && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Dependency health check failed: {dependencyCheck.error?.message ?? 'Unknown error'}
          </span>
        </div>
      )}
      <div className="divide-y rounded-lg border">
        <HealthCheckRow
          label="Startup"
          entry={health?.startup ?? null}
          configured={healthChecks?.startup}
          testId="plugin-health-startup"
        />
        <HealthCheckRow
          label="Liveness"
          entry={health?.liveness ?? null}
          configured={healthChecks?.liveness}
          testId="plugin-health-liveness"
        />
        <HealthCheckRow
          label="Peer Dependencies"
          entry={health?.dependencies ?? null}
          configured={hasDependencyCheck}
          testId="plugin-health-dependencies"
          alwaysExpandIfContent
          action={
            hasDependencyCheck ? (
              <Button
                variant="outline"
                size="toolbar-icon"
                onClick={() => dependencyCheck.mutate()}
                disabled={dependencyCheck.isPending}
                aria-label={dependencyButtonLabel}
                title={dependencyCheck.isPending ? undefined : dependencyButtonLabel}
              >
                {dependencyCheck.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <RotateCw className="size-3.5" aria-hidden />
                )}
              </Button>
            ) : undefined
          }
        />
      </div>
    </section>
  );
}
